/**
 * Client-side `window.dataLayer` push helpers for GTM events fired after
 * hydration (recs impressions, quick-view, etc.). SSR-time pushes use the
 * inline `<script>` components in `gtm-inline.tsx` instead.
 *
 * No-ops on the server, where `window` is undefined.
 *
 * SFCC-11011 (ANALYTICS-14 / -15).
 */
import { stampRouteContext } from "./page-context";
import { getCurrentPageContext } from "./page-context.client";

type DataLayerWindow = Window & { dataLayer?: Record<string, unknown>[] };

/**
 * Push a single event object onto `window.dataLayer` (client-only).
 *
 * COM-585: this is the one seam every client dataLayer push funnels through —
 * `pushEvent`, `pushEcommerceEvent`, `pushEcommerce`, `flushEcommerce` and
 * `pushPageType` all land here — so it is where a route-bound event gets the
 * context of the route it occurred on (C4).
 *
 * `gtm-sink.ts` was considered and rejected as the seam: it is a fork GATE
 * (`pushEcommerceEvent` returns early on `!isDataLayerForkEnabled`), and the
 * two headline route-bound events are not fork keys at all —
 * `GA4_productDetail` and `view_item_list` are deliberately always-on and
 * routed AWAY from the sink (see the note above `pushEcommerce`). Stamping
 * there would have made them fork-gateable.
 *
 * INERT UNLESS ENABLED: `window.__HEC_PAGE_CTX` is written only by the
 * page-context contract, which is off unless `WORKERS_PAGE_CONTEXT_MODE` is
 * `shadow`/`on` for an allowlisted site. With no context present
 * `stampRouteContext` returns the very same object, so this function behaves
 * byte-for-byte as it did before.
 */
export const pushDataLayer = (event: Record<string, unknown>): void => {
  if (typeof window === "undefined") return;
  const w = window as DataLayerWindow;
  w.dataLayer = w.dataLayer || [];
  // NEVER let stamping break an existing push. This seam sits on the critical
  // path of every analytics event in the app; a throw here would take out
  // pushes that work today, which is a strictly worse failure than an
  // unstamped event. Same principle the AB Tasty hit sender follows.
  let payload: Record<string, unknown> = event;
  try {
    payload = stampRouteContext(event, getCurrentPageContext());
  } catch {
    // Fall through with the original event, unstamped.
  }
  w.dataLayer.push(payload);
};

/**
 * Push `{ ecommerce: null }` to clear the previous ecommerce object before a
 * new ecommerce event fires. GA4 best practice, and SFRA parity for
 * `GA4_quickViewClick` (`dataLayerHelper.js:282` flushes before the push) —
 * without it, fields from a prior ecommerce event can bleed into the next.
 */
export const flushEcommerce = (): void => pushDataLayer({ ecommerce: null });

/**
 * Reset the previous ecommerce object, then push a new ecommerce-bearing event
 * in the same tick, so GTM's recursive (array-by-index) merge can't bleed a
 * prior event's nested `ecommerce` fields (items, value, list ids) into this
 * one on SPA navigation (SFCC-11890). Use for the always-on GA4 ecommerce
 * pushes — `GA4_productDetail`, `view_item_list`, etc.
 *
 * NOTE: distinct from `pushEcommerceEvent` in `event-layer/gtm-sink.ts`, which
 * flushes too but is gated behind the Segment-first dataLayer fork
 * (`isDataLayerForkEnabled`); this helper always fires.
 */
export const pushEcommerce = (event: Record<string, unknown>): void => {
  flushEcommerce();
  pushDataLayer(event);
};

/**
 * Push a new `ecomm_pagetype` (+ its `pageType` alias, SFCC-11889 C7) on SPA
 * navigation so it becomes the ONLY one in `window.dataLayer` — renaming any
 * prior entries to `prev_*` first (SFCC-11890).
 *
 * Why not a plain append: with AB Tasty's single-page-app mode on, AB Tasty
 * re-evaluates campaigns on soft nav but reads the **first** `ecomm_pagetype`
 * in the append-only dataLayer — i.e. the landing page's value — so a
 * homepage-targeted campaign keeps firing on every page you soft-nav to from
 * the homepage. Collapsing to a single current-value entry (SFRA's "one
 * `ecomm_pagetype` per page" invariant, which SFRA gets for free via full
 * reloads) makes AB Tasty's first-match read land on the current page.
 *
 * GTM/GA4-safe: GTM builds its model at push time (last value wins), so
 * mutating already-pushed array entries does not change GTM's model — and we
 * still push the current value normally, so last-value consumers stay correct.
 * The rename only changes what a raw-array reader (AB Tasty) sees, and it
 * preserves the prior values under `prev_ecomm_pagetype` for auditability.
 *
 * Hard loads don't need this (fresh dataLayer, single value); the SSR inline
 * push in `gtm-inline.tsx` stays a plain push. This runs only from the
 * client-side `PageTypeTracker` on route changes.
 */
export const pushPageType = (pageType: string): void => {
  if (typeof window === "undefined") return;
  const w = window as DataLayerWindow;
  const dl = w.dataLayer;
  if (dl) {
    for (const entry of dl) {
      if (!entry) continue;
      // `pageType` gets the same collapse treatment as `ecomm_pagetype` so
      // raw-array readers see exactly one current value for either key.
      for (const key of ["ecomm_pagetype", "pageType"]) {
        if (Object.prototype.hasOwnProperty.call(entry, key)) {
          entry[`prev_${key}`] = entry[key];
          delete entry[key];
        }
      }
    }
  }
  // Reuse the canonical push (lazy-inits the array, guards `window`).
  // `pageType` (SFCC-11889 C7) carries the same value under the key some GTM
  // tags/variables read; `ecomm_pagetype` stays the AB Tasty / remarketing key.
  pushDataLayer({ ecomm_pagetype: pageType, pageType });
};
